import svgPath from 'svgpath';
import type { Bounds } from '@/version-types';
import type { ReadyTextRenderHighlight } from '../../assets';
import { getPathBounds, parseStrictSVG } from '../../utils';
import type { HighlightFragment } from './fragments';

// cspell:ignore parsererror

type HighlightAsset = ReadyTextRenderHighlight;
type RenderContext = {
    fragment: HighlightFragment;
    asset: HighlightAsset;
    fontRatio: number;
    thicknessRatio: number;
    sourceWidth: number;
    sourceHeight: number;
};
type RenderSize = { width: number; height: number; paintedBounds?: Bounds };

const SHAPES = 'line,path,rect,use,circle,ellipse';

/**
 * 根据粗细配置计算分段线性插值。
 *
 * @param input - 输入百分比。
 * @param values - 1%、50% 与 100% 对应值。
 * @returns 插值结果。
 */
const linearInterpolation = (input: number, values: readonly number[]): number => {
    const points = [
        [0, 0],
        [1, values[0]],
        [50, values[1]],
        [100, values[2]],
    ];
    if (input <= points[0][0]) return points[0][1];
    if (input >= points.at(-1)![0]) return points.at(-1)![1];
    for (let index = 0; index < points.length - 1; index += 1) {
        const current = points[index];
        const next = points[index + 1];
        if (input < current[0] || input > next[0]) continue;
        const ratio = (input - current[0]) / (next[0] - current[0]);
        return current[1] + (next[1] - current[1]) * ratio;
    }
    return 0;
};

/**
 * 读取变换后 Highlight SVG 中可见形状的实际边界。
 *
 * @param root - 已变换的 Highlight SVG。
 * @param width - SVG 逻辑宽度。
 * @param height - SVG 逻辑高度。
 * @returns 同时覆盖逻辑盒与可见 path/rect 的边界。
 */
const getPaintedBounds = (root: SVGSVGElement, width: number, height: number): Bounds => {
    const rectBounds = Array.from(root.querySelectorAll('rect')).reduce<Bounds>(
        (bounds, rect) => {
            const x = Number(rect.getAttribute('x') || 0);
            const y = Number(rect.getAttribute('y') || 0);
            const strokeRadius = Number(rect.getAttribute('stroke-width') || 0) / 2;
            const right = x + Number(rect.getAttribute('width') || 0);
            const bottom = y + Number(rect.getAttribute('height') || 0);
            return {
                left: Math.min(bounds.left, x - strokeRadius),
                top: Math.min(bounds.top, y - strokeRadius),
                right: Math.max(bounds.right, right + strokeRadius),
                bottom: Math.max(bounds.bottom, bottom + strokeRadius),
            };
        },
        { left: 0, top: 0, right: width, bottom: height },
    );
    return Array.from(root.querySelectorAll('path')).reduce<Bounds>((bounds, path) => {
        const d = path.getAttribute('d');
        const stroke = path.getAttribute('stroke');
        if (!d || path.closest('defs')) return bounds;
        const { left, top, right, bottom } = getPathBounds(d);
        const radius = stroke && stroke !== 'none' ? Number(path.getAttribute('stroke-width') || 0) / 2 : 0;
        return {
            left: Math.min(bounds.left, left - radius),
            top: Math.min(bounds.top, top - radius),
            right: Math.max(bounds.right, right + radius),
            bottom: Math.max(bounds.bottom, bottom + radius),
        };
    }, rectBounds);
};

/**
 * 将 Highlight 主题色写入一个可绘制形状。
 *
 * @param element - 素材中的可绘制 SVG 元素。
 * @param color - 宿主声明的 Highlight 颜色。
 * @returns void，通过副作用更新 fill/stroke。
 */
const setColor = (element: Element, color: string): void => {
    if (element.getAttribute('fill') && element.getAttribute('fill') !== 'none') element.setAttribute('fill', color);
    if (element.getAttribute('stroke') && element.getAttribute('stroke') !== 'none')
        element.setAttribute('stroke', color);
};

/**
 * 更新 Highlight SVG 的逻辑尺寸。
 *
 * @param root - Highlight SVG 根节点。
 * @param width - 输出宽度。
 * @param height - 输出高度。
 * @returns void，通过副作用更新根节点。
 */
const setRootSize = (root: SVGSVGElement, width: number, height: number): void => {
    root.setAttribute('width', String(width));
    root.setAttribute('height', String(height));
};

/**
 * 把 Highlight SVG 放到文字片段对应位置。
 *
 * @param root - 已变换尺寸的 Highlight SVG。
 * @param context - 当前片段与素材缩放上下文。
 * @param size - 当前素材的输出尺寸与可选绘制边界。
 * @returns 根节点在文字坐标系内的左上位置。
 */
const positionRoot = (root: SVGSVGElement, context: RenderContext, size: RenderSize): { left: number; top: number } => {
    const { fragment, fontRatio } = context;
    const { config } = fragment.highlight;
    const { width, height } = size;
    const left = fragment.left + config.offset.x * fontRatio - (width - fragment.width) / 2;
    const offsetY = config.offset.y * fontRatio;
    const top =
        config.position === 'top'
            ? fragment.top + offsetY
            : config.position === 'bottom'
              ? fragment.top + fragment.height - height + offsetY
              : fragment.top + (fragment.height - height) / 2 + offsetY;
    root.setAttribute('x', String(left));
    root.setAttribute('y', String(top));
    root.setAttribute('data-text-highlight', '');
    root.setAttribute('overflow', 'visible');
    return { left, top };
};

/**
 * 按缩放比例更新 stroke-dasharray。
 *
 * @param element - 带虚线属性的 SVG 元素。
 * @param ratio - 字号缩放比例。
 * @returns void，通过副作用更新属性。
 */
const scaleDasharray = (element: Element, ratio: number): void => {
    const value = element.getAttribute('stroke-dasharray');
    if (!value) return;
    element.setAttribute(
        'stroke-dasharray',
        value
            .split(/\s+/)
            .map((item) => Number(item) * ratio)
            .join(' '),
    );
};

/**
 * 为 use 型素材的占位 ID 创建本轮唯一引用。
 *
 * @param shape - use 实际引用的素材形状。
 * @param root - Highlight SVG 根节点。
 * @returns 可复制的 use 节点；素材结构不完整时返回 null。
 */
const uniqueUseTarget = (shape: Element, root: SVGSVGElement): SVGUseElement | null => {
    const use = root.querySelector('use');
    if (!use) return null;
    const id = shape.getAttribute('id');
    if (id?.endsWith('$hash')) {
        const next = id.replace('$hash', crypto.randomUUID());
        shape.setAttribute('id', next);
        use.setAttribute('href', `#${next}`);
    }
    return use;
};

/**
 * 渲染 combined 类型 Highlight 素材。
 *
 * @param root - 已解析素材 SVG。
 * @param context - 当前片段与缩放上下文。
 * @returns 变换后的尺寸。
 */
const renderCombined = (root: SVGSVGElement, context: RenderContext): RenderSize => {
    const { fragment, fontRatio } = context;
    const positions = (root.getAttribute('data-content-position') || '0,0,0,0')
        .split(',')
        .map((value) => Number(value) * fontRatio);
    const [contentTop = 0, contentRight = 0, contentBottom = 0, contentLeft = 0] = positions;
    const contentHeight = root.hasAttribute('data-content-height')
        ? Number(root.getAttribute('data-content-height')) * fontRatio
        : fragment.height;
    const contentWidth = fragment.width + fragment.highlight.config.padding.x * 2 * fontRatio;
    const width = contentWidth + contentLeft + contentRight;
    const height = contentHeight + contentTop + contentBottom;
    const hash = crypto.randomUUID();
    root.querySelectorAll(SHAPES).forEach((shape) => {
        const id = shape.getAttribute('id');
        if (id?.endsWith('$hash')) shape.setAttribute('id', id.replace('$hash', hash));
        if (shape.tagName === 'path') {
            const d = shape.getAttribute('d');
            if (d) shape.setAttribute('d', svgPath(d).scale(fontRatio).toString());
        }
        setColor(shape, fragment.highlight.color);
        if (shape.hasAttribute('data-resize-width')) shape.setAttribute('width', String(contentWidth));
        if (shape.hasAttribute('data-resize-height')) shape.setAttribute('height', String(contentHeight));
        for (const [dataName, attr] of [
            ['data-base-x', 'x'],
            ['data-base-y', 'y'],
            ['data-base-stroke-width', 'stroke-width'],
        ] as const) {
            const value = shape.getAttribute(dataName);
            if (value) shape.setAttribute(attr, String(Number(value) * fontRatio));
        }
    });
    root.querySelectorAll('use').forEach((use) => {
        const href = use.getAttribute('href') || use.getAttribute('xlink:href');
        if (href?.endsWith('$hash')) use.setAttribute('href', href.replace('$hash', hash));
    });
    root.setAttribute('viewBox', `0 0 ${width} ${height}`);
    setRootSize(root, width, height);
    return { width, height };
};

/**
 * 渲染逐字符复制的 use 类型 Highlight。
 *
 * @param root - 已解析素材 SVG。
 * @param shape - use 引用的素材形状。
 * @param context - 当前片段与缩放上下文。
 * @returns 变换后的尺寸；结构不完整时返回 null。
 */
const renderUse = (root: SVGSVGElement, shape: Element, context: RenderContext): RenderSize | null => {
    const { fragment, fontRatio, thicknessRatio, sourceWidth, sourceHeight } = context;
    const use = uniqueUseTarget(shape, root);
    if (!use) return null;
    const shapeRatio = fontRatio * thicknessRatio;
    const width = fragment.width + fragment.highlight.config.padding.x * 2 * fontRatio;
    const height = sourceHeight * shapeRatio;
    const d = shape.getAttribute('d');
    if (d) shape.setAttribute('d', svgPath(d).scale(shapeRatio).toString());
    for (const attr of ['r', 'rx', 'ry', 'cx', 'cy'] as const) {
        const value = shape.getAttribute(attr);
        if (value) shape.setAttribute(attr, String(Number(value) * shapeRatio));
    }
    const translateX = shape.getAttribute('data-base-translate-x');
    const translateY = shape.getAttribute('data-base-translate-y');
    const style = shape.getAttribute('style');
    if (style && translateX != null && translateY != null) {
        shape.setAttribute(
            'style',
            style.replace(
                /translate\([^)]*\)/,
                `translate(${Number(translateX) * shapeRatio}px, ${Number(translateY) * shapeRatio}px)`,
            ),
        );
    }
    setColor(shape, fragment.highlight.color);
    root.setAttribute('viewBox', `0 0 ${width} ${height}`);
    setRootSize(root, width, height);
    let paintedLeft = 0;
    let paintedRight = width;
    fragment.children.forEach((child) => {
        const clone = use.cloneNode() as SVGUseElement;
        const x = child.left - fragment.left + (child.width - sourceWidth * shapeRatio) / 2;
        clone.setAttribute('x', String(x));
        root.appendChild(clone);
        paintedLeft = Math.min(paintedLeft, x);
        paintedRight = Math.max(paintedRight, x + sourceWidth * shapeRatio);
    });
    use.remove();
    return { width, height, paintedBounds: { left: paintedLeft, top: 0, right: paintedRight, bottom: height } };
};

/**
 * 按素材类型调整一个 Highlight 形状。
 *
 * @param root - 已解析素材 SVG。
 * @param shape - 当前待调整的素材形状。
 * @param context - 当前片段与缩放上下文。
 * @returns 变换后的尺寸；素材结构不适用时返回 null。
 */
const renderShape = (root: SVGSVGElement, shape: Element, context: RenderContext): RenderSize | null => {
    const { fragment, fontRatio, thicknessRatio, sourceWidth, sourceHeight } = context;
    const { config } = fragment.highlight;
    const strokeBase = Number(config.sizeRange[1] ?? shape.getAttribute('stroke-width') ?? 0);
    if (config.type === 'use') return renderUse(root, shape, context);
    if (shape.tagName === 'line') {
        let width = fragment.width;
        const height = Math.max(strokeBase * fontRatio * thicknessRatio, 0.1);
        const x1 = (Number(shape.getAttribute('x1') || 0) - config.padding.x) * fontRatio;
        const dash = shape.getAttribute('stroke-dasharray');
        if (dash) {
            const dashWidth = Number(dash.split(/\s+/)[0]) * fontRatio;
            const count = Math.ceil((width - x1) / dashWidth);
            width = (count + ((count + 1) % 2)) * dashWidth + x1 * 2;
            shape.setAttribute('stroke-dasharray', String(dashWidth));
        }
        shape.setAttribute('x1', String(x1));
        shape.setAttribute('x2', String(width - x1));
        shape.setAttribute('y1', String(height / 2));
        shape.setAttribute('y2', String(height / 2));
        shape.setAttribute('stroke-width', String(height));
        setColor(shape, fragment.highlight.color);
        root.setAttribute('viewBox', `0 0 ${width} ${height}`);
        setRootSize(root, width, height);
        return { width, height };
    }
    if (config.type === 'cover') {
        const width = fragment.width + config.padding.x * 2 * fontRatio;
        const height = fragment.height + config.padding.y * 2 * fontRatio;
        const d = shape.getAttribute('d');
        if (d)
            shape.setAttribute(
                'd',
                svgPath(d)
                    .scale(width / sourceWidth, height / sourceHeight)
                    .toString(),
            );
        if (shape.tagName === 'rect') {
            const x = Number(shape.getAttribute('x') || 0);
            const y = Number(shape.getAttribute('y') || 0);
            const rightInset = sourceWidth - x - Number(shape.getAttribute('width') || sourceWidth);
            const bottomInset = sourceHeight - y - Number(shape.getAttribute('height') || sourceHeight);
            shape.setAttribute('x', String(x * fontRatio));
            shape.setAttribute('y', String(y * fontRatio));
            shape.setAttribute('width', String(width - (x + rightInset) * fontRatio));
            shape.setAttribute('height', String(height - (y + bottomInset) * fontRatio));
            for (const attr of ['rx', 'ry'] as const) {
                const value = shape.getAttribute(attr);
                if (value) shape.setAttribute(attr, String(Number(value) * fontRatio));
            }
        }
        setColor(shape, fragment.highlight.color);
        if (config.enableSize) {
            shape.setAttribute('stroke-width', String(strokeBase * fontRatio * thicknessRatio));
            scaleDasharray(shape, fontRatio);
        }
        root.setAttribute('viewBox', `0 0 ${width} ${height}`);
        setRootSize(root, width, height);
        return { width, height };
    }
    if (config.type === 'repeat' && shape.tagName === 'path') {
        const d = shape.getAttribute('d');
        if (!d) return null;
        const pathBox = getPathBounds(d);
        const overlap = pathBox.right - pathBox.left - sourceWidth;
        const step = Math.max(
            Math.ceil(
                (fragment.width + (overlap + config.padding.x * 2) * fontRatio) / ((sourceWidth + overlap) * fontRatio),
            ),
            1,
        );
        const repeated = (config.repeatPath || '').repeat(step);
        const nextPath = config.repeatPath ? d.replace(new RegExp(config.repeatPath, 'g'), repeated) : d;
        const width = step * (sourceWidth + overlap) * fontRatio - overlap * fontRatio;
        const height = sourceHeight * fontRatio;
        shape.setAttribute('d', svgPath(nextPath).scale(fontRatio).toString());
        shape.setAttribute('fill', 'none');
        shape.setAttribute('stroke-width', String(strokeBase * fontRatio * thicknessRatio));
        setColor(shape, fragment.highlight.color);
        root.setAttribute('viewBox', `0 0 ${width} ${height}`);
        setRootSize(root, width, height);
        return { width, height };
    }
    const width = fragment.width + config.padding.x * 2 * fontRatio;
    const height = (sourceHeight + config.padding.y * 2) * fontRatio;
    if (shape.tagName === 'path') {
        const d = shape.getAttribute('d');
        if (d && !config.forceStretch)
            shape.setAttribute(
                'd',
                svgPath(d)
                    .scale(width / sourceWidth, height / sourceHeight)
                    .toString(),
            );
        else if (config.forceStretch) root.setAttribute('preserveAspectRatio', 'none');
    }
    if (shape.tagName === 'rect') {
        shape.setAttribute('width', String(width));
        shape.setAttribute('height', String(height));
        for (const attr of ['x', 'y', 'rx', 'ry'] as const) {
            const value = shape.getAttribute(attr);
            if (value) shape.setAttribute(attr, String(Number(value) * fontRatio));
        }
        scaleDasharray(shape, fontRatio);
    }
    shape.setAttribute('stroke-width', String(strokeBase * fontRatio * thicknessRatio));
    setColor(shape, fragment.highlight.color);
    if (!config.forceStretch) root.setAttribute('viewBox', `0 0 ${width} ${height}`);
    setRootSize(root, width, height);
    return { width, height };
};

/**
 * 基于片段和 ready SVG 独立生产 Highlight 节点。
 *
 * @param fragment - 待绘制的 Highlight 片段。
 * @param asset - 已加载完成的 Highlight 素材与配置。
 * @returns Highlight SVG 节点及其视觉边界；素材无效时返回 null。
 */
export const renderHighlight = (
    fragment: HighlightFragment,
    asset: HighlightAsset,
): { node: SVGSVGElement; bounds: Bounds } | null => {
    const root = parseStrictSVG(asset.svgText);
    if (!root) return null;
    const viewBox = root
        .getAttribute('viewBox')
        ?.split(/[\s,]+/)
        .map(Number);
    if (!viewBox || viewBox.length !== 4 || viewBox.some((value) => !Number.isFinite(value))) return null;
    const sourceWidth = viewBox[2];
    const sourceHeight = viewBox[3];
    if (sourceWidth <= 0 || sourceHeight <= 0) return null;
    const { config } = fragment.highlight;
    const fontRatio = fragment.fontSize / config.defaultFontSize;
    const thicknessRatio =
        config.enableSize && config.sizeRange.length === 3 && config.sizeRange[1] !== 0
            ? linearInterpolation(fragment.highlight.size, config.sizeRange) / config.sizeRange[1]
            : 1;
    const context = { fragment, asset, fontRatio, thicknessRatio, sourceWidth, sourceHeight };
    const size =
        config.type === 'combined'
            ? renderCombined(root, context)
            : Array.from(root.querySelectorAll(SHAPES)).reduce<RenderSize | null>(
                  (result, shape) => renderShape(root, shape, context) ?? result,
                  null,
              );
    if (!size || size.width <= 0 || size.height <= 0) return null;
    const { left, top } = positionRoot(root, context, size);
    const shapeBounds = getPaintedBounds(root, size.width, size.height);
    const paintedBounds = size.paintedBounds
        ? {
              left: Math.min(shapeBounds.left, size.paintedBounds.left),
              top: Math.min(shapeBounds.top, size.paintedBounds.top),
              right: Math.max(shapeBounds.right, size.paintedBounds.right),
              bottom: Math.max(shapeBounds.bottom, size.paintedBounds.bottom),
          }
        : shapeBounds;
    return {
        node: root,
        bounds: {
            left: left + paintedBounds.left,
            top: top + paintedBounds.top,
            right: left + paintedBounds.right,
            bottom: top + paintedBounds.bottom,
        },
    };
};
